import { formatStage, formatPollInterval, formatTimeAgo } from '../utils/formatters';
import type { EventLifecycleResponse } from '../../../api/client';

interface LifecycleStatusProps {
  lifecycleData: EventLifecycleResponse | null | undefined;
  commenceTime?: string;
  isLive?: boolean;
}

/**
 * Lifecycle & Polling Status panel
 * Shows current stage, polling tier, historical data status, and last polled time
 */
export default function LifecycleStatus({ lifecycleData, commenceTime: _commenceTime, isLive }: LifecycleStatusProps) {
  const lifecycle = lifecycleData?.lifecycle;
  const pollingTier = lifecycleData?.pollingTier;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">⏱️</span>
        <h2 className="text-lg font-semibold">Lifecycle & Polling Status</h2>
        <span className="ml-auto text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
          Timezone: UTC
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Current Stage */}
        <div className="bg-surface-50 rounded-lg p-4">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Current Stage</div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${
              lifecycle?.currentStage === 'live' || isLive ? 'bg-red-500/20 text-red-400' :
              lifecycle?.currentStage === 'pre_game' ? 'bg-yellow-500/20 text-yellow-400' :
              lifecycle?.currentStage === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
              lifecycle?.currentStage === 'completed' ? 'bg-gray-500/20 text-gray-400' :
              'bg-gray-700/20 text-gray-500'
            }`}>
              {(lifecycle?.currentStage === 'live' || isLive) && (
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              )}
              {isLive ? 'Live' : formatStage(lifecycle?.currentStage)}
            </span>
          </div>
        </div>

        {/* Polling Tier */}
        <div className="bg-surface-50 rounded-lg p-4">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Polling Tier</div>
          <div className="flex flex-col">
            <span className={`text-sm font-medium ${
              pollingTier?.name === 'live' ? 'text-red-400' :
              pollingTier?.name === 'soon' || pollingTier?.name === 'urgent' ? 'text-yellow-400' :
              pollingTier?.name === 'later' ? 'text-blue-400' :
              pollingTier?.name === 'skip' ? 'text-gray-400' :
              'text-gray-500'
            }`}>
              {pollingTier?.name?.toUpperCase() || 'Unknown'}
            </span>
            <span className="text-xs text-gray-500 mt-1">
              {pollingTier?.intervalSeconds
                ? `Every ${formatPollInterval(pollingTier.intervalSeconds)}`
                : 'No polling'}
            </span>
          </div>
        </div>

        {/* Historical Data Status */}
        <div className="bg-surface-50 rounded-lg p-4">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Historical Data</div>
          <div className="flex items-center gap-2">
            {lifecycle?.historicalDownloaded ? (
              <>
                <span className="w-2.5 h-2.5 bg-green-500 rounded-full"></span>
                <span className="text-sm text-green-400">Imported</span>
              </>
            ) : (
              <>
                <span className="w-2.5 h-2.5 bg-yellow-500 rounded-full"></span>
                <span className="text-sm text-yellow-400">Pending</span>
              </>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Odds API & Polymarket
          </div>
        </div>

        {/* Last Polled */}
        <div className="bg-surface-50 rounded-lg p-4">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Last Polled</div>
          <div className="text-sm text-gray-300">
            {lifecycle?.lastPolled
              ? formatTimeAgo(lifecycle.lastPolled)
              : 'Never'}
          </div>
          {lifecycle?.liveDetection?.reason && (
            <div className="text-xs text-gray-500 mt-1">
              Live via: {lifecycle.liveDetection.reason}
            </div>
          )}
        </div>
      </div>

      {/* Polling Tier Legend */}
      <div className="mt-4 pt-4 border-t border-gray-800">
        <div className="text-xs text-gray-400 mb-2">Polling Tiers (Odds API & Polymarket aligned):</div>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            <span className="text-gray-400">Scheduled (&gt;2h): 15 min</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
            <span className="text-gray-400">Pre-game (&lt;2h): 10 min</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            <span className="text-gray-400">Live: 1 min</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
            <span className="text-gray-400">Completed: No polling</span>
          </div>
        </div>
      </div>
    </div>
  );
}
